import type {
  AdapterIoFilterCountsApiData,
  AdapterIoFilterCountsApiResponses,
  AdapterIoResultGetApiData,
  AdapterIoResultGetApiResponses,
  AdapterIoResultListApiData,
  AdapterIoResultListApiResponses,
  AdapterIoResultResolveApiData,
  AdapterIoResultResolveApiResponses,
  AdapterIoResultRetryApiData,
  AdapterIoResultRetryApiResponses,
  AdapterRecallExclusionCreateApiData,
  AdapterRecallExclusionCreateApiResponses,
  AdapterRecallExclusionDeleteApiData,
  AdapterRecallExclusionDeleteApiResponses,
  AdapterRecallExclusionListApiData,
  AdapterRecallExclusionListApiResponses,
  AdapterRecallExclusionUpdateApiData,
  AdapterRecallExclusionUpdateApiResponses,
  AdapterReviewCaseActionApiData,
  AdapterReviewCaseActionApiResponses,
  AdapterReviewCaseAssignApiData,
  AdapterReviewCaseAssignApiResponses,
  AdapterReviewCaseDismissApiData,
  AdapterReviewCaseDismissApiResponses,
  AdapterReviewCaseEventsApiData,
  AdapterReviewCaseEventsApiResponses,
  AdapterReviewCaseFilterCountsApiData,
  AdapterReviewCaseFilterCountsApiResponses,
  AdapterReviewCaseGetApiData,
  AdapterReviewCaseGetApiResponses,
  AdapterReviewCaseListApiData,
  AdapterReviewCaseListApiResponses,
  AdapterReviewCaseResolveApiData,
  AdapterReviewCaseResolveApiResponses,
  AdapterSyncJobListApiData,
  AdapterSyncJobListApiResponses,
  AdapterSyncJobRunApiData,
  AdapterSyncJobRunApiResponses
} from '@navio/nsdk';

import { apiUrl, dataEndpoint, responseEndpoint } from '../defineResource';

/** Adapter I/O results (push + pull) + sync job catalog/trigger. */
export const getAdapterUrls = {
  getAdapterReviewCases: dataEndpoint<AdapterReviewCaseListApiData, AdapterReviewCaseListApiResponses>({
    getUrl: (queryParams) => apiUrl('review-cases/', queryParams)
  }),
  getAdapterReviewCaseFilterCounts: dataEndpoint<
    AdapterReviewCaseFilterCountsApiData,
    AdapterReviewCaseFilterCountsApiResponses
  >({
    getUrl: (queryParams) => apiUrl('review-cases/filter-counts/', queryParams)
  }),
  getAdapterReviewCase: dataEndpoint<AdapterReviewCaseGetApiData, AdapterReviewCaseGetApiResponses>({
    getUrl: ({ caseId }) => apiUrl(`review-cases/${caseId}/`)
  }),
  getAdapterReviewCaseEvents: dataEndpoint<AdapterReviewCaseEventsApiData, AdapterReviewCaseEventsApiResponses>({
    getUrl: ({ caseId }) => apiUrl(`review-cases/${caseId}/events/`)
  }),
  postAdapterReviewCaseResolve: responseEndpoint<AdapterReviewCaseResolveApiData, AdapterReviewCaseResolveApiResponses>(
    {
      getUrl: ({ caseId }) => apiUrl(`review-cases/${caseId}/resolve/`),
      getBody: ({ note }) => ({ note })
    }
  ),
  postAdapterReviewCaseDismiss: responseEndpoint<AdapterReviewCaseDismissApiData, AdapterReviewCaseDismissApiResponses>(
    {
      getUrl: ({ caseId }) => apiUrl(`review-cases/${caseId}/dismiss/`),
      getBody: ({ note }) => ({ note })
    }
  ),
  postAdapterReviewCaseAssign: responseEndpoint<AdapterReviewCaseAssignApiData, AdapterReviewCaseAssignApiResponses>({
    getUrl: ({ caseId }) => apiUrl(`review-cases/${caseId}/assign/`),
    getBody: ({ assignedToId }) => (assignedToId != null ? { assignedToId } : {})
  }),
  postAdapterReviewCaseAction: responseEndpoint<AdapterReviewCaseActionApiData, AdapterReviewCaseActionApiResponses>({
    getUrl: ({ caseId }) => apiUrl(`review-cases/${caseId}/actions/`),
    getBody: ({ action }) => ({ action })
  }),
  getAdapterIoResults: dataEndpoint<AdapterIoResultListApiData, AdapterIoResultListApiResponses>({
    getUrl: (queryParams) => apiUrl('io-results/', queryParams)
  }),
  getAdapterIOFilterCounts: dataEndpoint<AdapterIoFilterCountsApiData, AdapterIoFilterCountsApiResponses>({
    getUrl: (queryParams) => apiUrl('io-results/filter-counts/', queryParams)
  }),
  getAdapterIoResult: dataEndpoint<AdapterIoResultGetApiData, AdapterIoResultGetApiResponses>({
    getUrl: ({ ioResultId }) => apiUrl(`io-results/${ioResultId}/`)
  }),
  postAdapterIoResultRetry: responseEndpoint<AdapterIoResultRetryApiData, AdapterIoResultRetryApiResponses>({
    getUrl: ({ ioResultId }) => apiUrl(`io-results/${ioResultId}/retry/`)
  }),
  postAdapterIoResultResolve: responseEndpoint<AdapterIoResultResolveApiData, AdapterIoResultResolveApiResponses>({
    getUrl: ({ ioResultId }) => apiUrl(`io-results/${ioResultId}/resolve/`),
    getBody: ({ resolutionNote }) => ({ resolutionNote })
  }),
  getAdapterSyncJobs: dataEndpoint<AdapterSyncJobListApiData, AdapterSyncJobListApiResponses>({
    getUrl: () => apiUrl('sync-jobs/')
  }),
  postAdapterSyncJobRun: responseEndpoint<AdapterSyncJobRunApiData, AdapterSyncJobRunApiResponses>({
    getUrl: () => apiUrl('sync-jobs/run/'),
    getBody: ({ jobKeys, serviceDepartmentIds, args }) => ({
      jobKeys,
      serviceDepartmentIds,
      ...(args != null && args.length > 0 ? { args } : {})
    })
  }),
  getAdapterRecallExclusions: dataEndpoint<AdapterRecallExclusionListApiData, AdapterRecallExclusionListApiResponses>({
    getUrl: () => apiUrl('adapter-recall-exclusions/')
  }),
  postAdapterRecallExclusion: responseEndpoint<
    AdapterRecallExclusionCreateApiData,
    AdapterRecallExclusionCreateApiResponses
  >({
    getUrl: () => apiUrl('adapter-recall-exclusions/'),
    getBody: ({ externalModelIdentifierId }) => ({ externalModelIdentifierId })
  }),
  patchAdapterRecallExclusion: responseEndpoint<
    AdapterRecallExclusionUpdateApiData,
    AdapterRecallExclusionUpdateApiResponses
  >({
    getUrl: ({ exclusionId }) => apiUrl(`adapter-recall-exclusions/${exclusionId}/`),
    getBody: ({ externalModelIdentifierId }) => ({ externalModelIdentifierId })
  }),
  deleteAdapterRecallExclusion: dataEndpoint<
    AdapterRecallExclusionDeleteApiData,
    AdapterRecallExclusionDeleteApiResponses
  >({
    getUrl: ({ exclusionId }) => apiUrl(`adapter-recall-exclusions/${exclusionId}/`)
  })
};
